
export type DebounceSimple<T extends (...args: any[]) => void> = (...args: Parameters<T>) => void;
export type DebounceWithCancel<T extends (...args: any[]) => void> = DebounceSimple<T> & { cancel: () => void };


/**
 * Class to implement debounce utilities
 */
export class Debounce
{
    /**
     * Method to create a simple debounced function
     *
     * The returned function will only call the original
     * function after the specified delay has elapsed since
     * the last call to the debounced function.
     *
     * @template T - The type of the function to debounce
     * @param {T} fn - The function to debounce
     * @param {number} delay - The debounce delay in milliseconds
     * @returns {DebounceSimple<T>} - The debounced input function
     */
    public static simple<T extends (...args: any[]) => void>(
        fn: T,
        delay: number
    ): DebounceSimple<T> {
        let timeout: number | null = null;
        return (...args: Parameters<T>) => {
            if (timeout) clearTimeout(timeout);
            timeout = window.setTimeout(() => fn(...args), delay);
        };
    }

    /**
     * Method to create a cancellable debounced function
     *
     * The returned function will only call the original
     * function after the specified delay has elapsed since
     * the last call to the debounced function.
     *
     * The `cancel` method can be used to cancel any pending invocations.
     *
     * @template T - The type of the function to debounce
     * @param {T} fn - The function to debounce
     * @param {number} delay - The debounce delay in milliseconds
     * @returns {DebounceWithCancel<T>} - The debounced input function
     */
    public static cancellable<T extends (...args: any[]) => void>(
        fn: T,
        delay: number
    ): DebounceWithCancel<T> {
        let timeout: number | null = null;
        const debounced = (...args: Parameters<T>) => {
            if (timeout) clearTimeout(timeout);
            timeout = window.setTimeout(() => fn(...args), delay);
        };

        debounced.cancel = () => {
            if (timeout) clearTimeout(timeout);
            timeout = null;
        };

        return debounced as typeof debounced & { cancel: () => void };
    }
}
